import type { FuelSavingApplyPayload } from '@makemydeal/dr-activities-fuel-saving';
import type { MergeFuelEconomyFromApiPayload, FuelEconomyApiVehicle } from '@makemydeal/dr-dash-store/types/fuelEconomyTypes';

export const parseMpg = (value: string | undefined): number | undefined => {
    if (value === undefined) {
        return undefined;
    }

    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) {
        return undefined;
    }

    if (parsed <= 0) {
        return undefined;
    }

    return parsed;
};

/**
 * Transforms the fuel-saving apply payload into a MergeFuelEconomyFromApi
 * payload that the reducer uses to persist MPG values onto state.vehicle
 * and state.tradeIn so they flow into the next saveOffer call.
 *
 * The slideout is only shown when at least one trade-in exists (guarded by
 * `showFuelSavingButton = isFuelSavingEnabled && hasTradeIns`), so
 * `payload.trades[0]` is always defined when this function is called.
 * newVehicleMpg is a shared UI field — the same value appears on every trade.
 */
export const buildFuelEconomyMergePayload = (payload: FuelSavingApplyPayload): MergeFuelEconomyFromApiPayload => {
    // Handle both single-vehicle (legacy) and multi-vehicle (new) payload formats
    const trades = payload.trades ?? [];
    const vehiclesWithTrades = payload.allVehicles
        ? payload.allVehicles
        : payload.vehicle
        ? [{ vehicle: payload.vehicle, trades }]
        : [];
    const allTrades = vehiclesWithTrades.flatMap((entry) => entry.trades);

    const sellingVehicles: FuelEconomyApiVehicle[] = vehiclesWithTrades
        .map((entry): FuelEconomyApiVehicle | undefined => {
            const firstTrade = entry.trades[0];
            if (!entry.vehicle.id || !firstTrade?.values) {
                return undefined;
            }

            return {
                vin: entry.vehicle.id,
                ok: true,
                fuelEconomy: { combined: parseMpg(firstTrade.values.newVehicleMpg) }
            };
        })
        .filter((vehicle): vehicle is FuelEconomyApiVehicle => !!vehicle);

    const tradeInVehicles: FuelEconomyApiVehicle[] = allTrades
        .filter((t) => t.vin && t.values)
        .map((t) => ({ vin: t.vin!, ok: true, fuelEconomy: { combined: parseMpg(t.values.tradeInMpg) } }));

    return { sellingVehicles, tradeInVehicles };
};
